import { GENRE_SOURCE_MAP } from '@intelligence/v3/utils/genre';
import { Relation } from '@intelligence/components/GenreSelect/enums';
import { GENRE_SOURCE_MAP as GENRE_SOURCE_MAP_SELECT } from '@intelligence/components/GenreSelect/constants';

import type { IGenreRes, IMainGenre } from '@intelligence/types/common';
import type {
  IGenreSelectValue,
  IGenreSource,
  IGenreSourcesGenreOptions,
  IGenreValuable,
  IOptionWithChildren,
} from '@intelligence/components/GenreSelect/types';
import type { IGenreSelectorPayload, MainGenre, SubGenre } from '@intelligence/components/GenreSelectMiddleware/types';

/**
 * 获取品类数据源类型与主、副品类Options集合，其中主、副品类Options集合均为空集合
 * @param sources 品类数据源类型集合
 * @returns 品类数据源类型与主、副品类Options集合，其中主、副品类Options集合均为空集合
 */
export const getEmptyGenreSourcesOptions = (sources?: IGenreSource[]): IGenreSourcesGenreOptions => {
  if (!Array.isArray(sources) || !sources.length) return {};
  return sources.reduce((acc, cur) => {
    if (!cur) return acc;
    return { ...acc, [cur]: [] };
  }, {});
};

export const GENRE_SOURCE_REVERSE_MAP: IRecord<string> = {
  iegg: 'ieg',
  sensortower: 'sensortower',
  appmagic: 'app_magic',
  annie: 'annir',
};

/**
 * 转换接口返回的品类数据源类型为符合GenreSelect组件的数据格式
 * 转换品类数据源类型ieg为标准的iegg、annir为标转的annie
 * @param res 品类数据源类型与主、副品类Options集合的map映射（接口返回的）
 * @returns 品类数据源类型与主、副品类Options集合的map映射（品类数据源类型符合GenreSelect组件要求的）
 */
export const formatGenreSourcesOfOptions = (res: IGenreRes): Record<IGenreSource, IMainGenre[]> => {
  if (!res) return { iegg: [], annie: [] };
  const resKeys = Object.keys(res) as (keyof IGenreRes)[];
  return resKeys.reduce((current, next) => {
    if (!next) return current;
    const genreSource = GENRE_SOURCE_MAP[next] || next;
    const genreSourceOptions = res[next] || [];
    return { ...current, [genreSource]: genreSourceOptions };
  }, {});
};

/**
 * 根据GenreSelect组件value生成GenreSelector组件onChange、onConfirm向组件调用者回传的payload对象的mainGenres、subGenres、level3Genres、level4Genres、level5Genres
 * @param value GenreSelect组件value
 * @param options 主、副品类Options集合
 * @return GenreSelector组件onChange、onConfirm向组件调用者回传的payload对象的mainGenres、subGenres、level3Genres、level4Genres、level5Genres
 */
export const getMainGenresAndSubGenres = (
  value: IGenreSelectValue,
  options: IOptionWithChildren[],
): Pick<IGenreSelectorPayload, 'mainGenres' | 'subGenres' | 'level3Genres' | 'level4Genres' | 'level5Genres'> => {
  const genres = value?.genres;
  const mainGenresResult: MainGenre[] = [];
  const subGenresResult: SubGenre[] = [];
  const level3GenresResult: SubGenre[] = [];
  const level4GenresResult: SubGenre[] = [];
  const level5GenresResult: SubGenre[] = [];

  if (!Array.isArray(genres) || !genres.length) {
    return { mainGenres: [], subGenres: [], level3Genres: [], level4Genres: [], level5Genres: [] };
  }

  for (const genre of genres) {
    const genreValue = genre?.value;
    const genreRelation = genre?.relation;
    const subGenres = genre?.children;
    const subGenresLength = subGenres?.length || 0;
    const genreOption = options?.find((option) => option?.value === genreValue);
    const genreTextEN = genreOption?.text;
    const subOptions = genreOption?.children;
    const genreIndeterminate = subGenresLength > 0 && subGenresLength !== subOptions?.length;
    if (!genreValue) continue;
    if (!Array.isArray(subGenres) || !subGenres.length) continue;

    // 遍历第2层级（subGenres）
    for (const subGenre of subGenres) {
      const subGenreValue = subGenre?.value;
      const subGenreOption = subOptions?.find((subOption) => subOption?.value === subGenreValue);
      const subGenreTextEN = subGenreOption?.text;
      // @ts-ignore
      const level3Genres = subGenre?.children;

      subGenresResult.push({
        value: subGenreValue,
        parentValue: genreValue,
        name: subGenreTextEN,
        // @ts-ignore
        textEn: subGenreTextEN,
        textCn: subGenreOption?.textCN,
        checked: true,
      });

      // 遍历第3层级（level3Genres）
      if (Array.isArray(level3Genres) && level3Genres.length > 0) {
        for (const level3Genre of level3Genres) {
          const level3GenreValue = level3Genre?.value;
          // @ts-ignore
          const level3GenreOption = subGenreOption?.children?.find(
            (l3Option: any) => l3Option?.value === level3GenreValue,
          );
          const level3GenreTextEN = level3GenreOption?.text;
          const level4Genres = level3Genre?.children;

          level3GenresResult.push({
            value: level3GenreValue,
            parentValue: genreValue,
            parent2Value: subGenreValue,
            name: level3GenreTextEN,
            // @ts-ignore
            textEn: level3GenreTextEN,
            textCn: level3GenreOption?.textCN,
            checked: true,
          });

          // 遍历第4层级（level4Genres）
          if (Array.isArray(level4Genres) && level4Genres.length > 0) {
            for (const level4Genre of level4Genres) {
              const level4GenreValue = level4Genre?.value;
              const level4GenreOption = level3GenreOption?.children?.find(
                (l4Option: any) => l4Option?.value === level4GenreValue,
              );
              const level4GenreTextEN = level4GenreOption?.text;
              // @ts-ignore
              const level5Genres = level4Genre?.children;

              level4GenresResult.push({
                value: level4GenreValue,
                parentValue: genreValue,
                parent2Value: subGenreValue,
                parent3Value: level3GenreValue,
                name: level4GenreTextEN,
                // @ts-ignore
                textEn: level4GenreTextEN,
                textCn: level4GenreOption?.textCN,
                checked: true,
              });

              // 遍历第5层级（level5Genres）
              if (Array.isArray(level5Genres) && level5Genres.length > 0) {
                for (const level5Genre of level5Genres) {
                  const level5GenreValue = level5Genre?.value;
                  const level5GenreOption = level4GenreOption?.children?.find(
                    (l5Option: any) => l5Option?.value === level5GenreValue,
                  );
                  const level5GenreTextEN = level5GenreOption?.text;

                  level5GenresResult.push({
                    value: level5GenreValue,
                    parentValue: genreValue,
                    parent2Value: subGenreValue,
                    parent3Value: level3GenreValue,
                    parent4Value: level4GenreValue,
                    name: level5GenreTextEN,
                    // @ts-ignore
                    textEn: level5GenreTextEN,
                    textCn: level5GenreOption?.textCN,
                    checked: true,
                  });
                }
              }
            }
          }
        }
      }
    }

    mainGenresResult.push({
      value: genreValue,
      relation: genreRelation,
      name: genreTextEN,
      // @ts-ignore
      textCn: genreOption?.textCN,
      textEn: genreTextEN,
      checked: !genreIndeterminate,
      indeterminate: genreIndeterminate,
    });
  }

  return {
    mainGenres: mainGenresResult,
    subGenres: subGenresResult,
    level3Genres: level3GenresResult,
    level4Genres: level4GenresResult,
    level5Genres: level5GenresResult,
  };
};

/**
 * 根据GenreSelector组件value 生成 GenreSelect组件value
 * @param payload.source 品类数据源类型
 * @param payload.mainGenres 主品类集合
 * @param payload.subGenres 副品类集合
 * @param payload.level3Genres 第3层级品类集合
 * @param payload.level4Genres 第4层级品类集合
 * @param payload.level5Genres 第5层级品类集合
 * @return GenreSelect组件value
 */
export const getGenreSelectValue = (payload: Omit<IGenreSelectorPayload, 'options'>): IGenreSelectValue => {
  const source = payload?.source;
  const mainGenres = payload?.mainGenres || [];
  const subGenres = payload?.subGenres || [];
  const level3Genres = payload?.level3Genres || [];
  const level4Genres = payload?.level4Genres || [];
  const level5Genres = payload?.level5Genres || [];

  // 构建主品类映射
  const mainGenresMap = mainGenres?.reduce<Record<string, MainGenre>>((acc, cur) => {
    const mainGenreValue = cur?.value;
    if (!mainGenreValue) return acc;
    return { ...acc, [mainGenreValue]: cur };
  }, {});

  // 构建第3层级品类映射,键为完整链路: parentValue_parent2Value
  const level3GenresMap = level3Genres?.reduce<Record<string, SubGenre[]>>((acc, cur) => {
    const parentValue = cur?.parentValue;
    const parent2Value = cur?.parent2Value;
    if (!parentValue || !parent2Value) return acc;
    const key = `${parentValue}__${parent2Value}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(cur);
    return acc;
  }, {});

  // 构建第4层级品类映射,键为完整链路: parentValue_parent2Value_parent3Value
  const level4GenresMap = level4Genres?.reduce<Record<string, SubGenre[]>>((acc, cur) => {
    const parentValue = cur?.parentValue;
    const parent2Value = cur?.parent2Value;
    const parent3Value = cur?.parent3Value;
    if (!parentValue || !parent2Value || !parent3Value) return acc;
    const key = `${parentValue}__${parent2Value}__${parent3Value}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(cur);
    return acc;
  }, {});

  // 构建第5层级品类映射,键为完整链路: parentValue_parent2Value_parent3Value_parent4Value
  const level5GenresMap = level5Genres?.reduce<Record<string, SubGenre[]>>((acc, cur) => {
    const parentValue = cur?.parentValue;
    const parent2Value = cur?.parent2Value;
    const parent3Value = cur?.parent3Value;
    const parent4Value = cur?.parent4Value;
    if (!parentValue || !parent2Value || !parent3Value || !parent4Value) return acc;
    const key = `${parentValue}__${parent2Value}__${parent3Value}__${parent4Value}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(cur);
    return acc;
  }, {});

  const genres: IGenreValuable[] = [];

  // 遍历副品类(第2层级)
  for (const subGenre of subGenres) {
    const subGenreValue = subGenre?.value;
    const genreValue = subGenre?.parentValue;
    const genreData = mainGenresMap?.[genreValue];
    if (!genreValue || !subGenreValue) continue;

    // 查找或创建主品类节点
    let genre = genres.find((g) => g?.value === genreValue);
    if (!genre) {
      genre = {
        relation: (genreData?.relation as Relation) || Relation.Or,
        value: genreValue,
        children: [],
      };
      genres.push(genre);
    }

    // 创建副品类节点
    const subGenreNode: any = { value: subGenreValue };

    // 查找该副品类下的第3层级品类,使用完整链路键
    const level3Key = `${genreValue}__${subGenreValue}`;
    const level3GenresOfSub = level3GenresMap?.[level3Key] || [];
    if (level3GenresOfSub.length > 0) {
      subGenreNode.children = [];
      for (const level3Genre of level3GenresOfSub) {
        const level3GenreValue = level3Genre?.value;
        if (!level3GenreValue) continue;

        // 创建第3层级品类节点
        const level3GenreNode: any = { value: level3GenreValue };

        // 查找该第3层级品类下的第4层级品类,使用完整链路键
        const level4Key = `${genreValue}__${subGenreValue}__${level3GenreValue}`;
        const level4GenresOfLevel3 = level4GenresMap?.[level4Key] || [];
        if (level4GenresOfLevel3.length > 0) {
          level3GenreNode.children = [];
          for (const level4Genre of level4GenresOfLevel3) {
            const level4GenreValue = level4Genre?.value;
            if (!level4GenreValue) continue;
            const level4GenreNode: any = { value: level4GenreValue };

            // 查找该第4层级品类下的第5层级品类,使用完整链路键
            const level5Key = `${genreValue}__${subGenreValue}__${level3GenreValue}__${level4GenreValue}`;
            const level5GenresOfLevel4 = level5GenresMap?.[level5Key] || [];
            if (level5GenresOfLevel4.length > 0) {
              level4GenreNode.children = [];
              for (const level5Genre of level5GenresOfLevel4) {
                const level5GenreValue = level5Genre?.value;
                if (!level5GenreValue) continue;
                level4GenreNode.children.push({ value: level5GenreValue });
              }
            }

            level3GenreNode.children.push(level4GenreNode);
          }
        }

        subGenreNode.children.push(level3GenreNode);
      }
    }

    genre.children.push(subGenreNode);
  }

  return {
    source,
    genres,
  };
};

/**
 * 根据品类数据源类型生成 GenreSelect 组件 value
 * @param payload 品类参数
 * @return GenreSelect 组件 value
 */
export const getGenreSelectValueBySource = (payload: Omit<IGenreSelectorPayload, 'options'>): IGenreSelectValue => {
  // App Magic 使用专门的构建方法，支持多级联结构
  if (payload?.source === GENRE_SOURCE_MAP_SELECT.APP_MAGIC) {
    return getGenreSelectValueForAppMagic(payload);
  }
  // 其他数据源使用原方法
  return getGenreSelectValue(payload);
};

/**
 * 专门为 App Magic 多级联结构构建 GenreSelect 组件 value
 * 与 getGenreSelectValue 的区别：
 * 1. 从 mainGenres 开始遍历，而不是 subGenres
 * 2. 支持只有部分层级的情况（如只有 mainGenres，没有 subGenres）
 * 3. 根据 subGenres/level3Genres/level4Genres/level5Genres 构建完整的品类路径
 *
 * @param payload.source 品类数据源类型
 * @param payload.mainGenres 主品类集合
 * @param payload.subGenres 副品类集合
 * @param payload.level3Genres 第3层级品类集合
 * @param payload.level4Genres 第4层级品类集合
 * @param payload.level5Genres 第5层级品类集合
 * @return GenreSelect 组件 value
 */
export const getGenreSelectValueForAppMagic = (payload: Omit<IGenreSelectorPayload, 'options'>): IGenreSelectValue => {
  const source = payload?.source;
  const mainGenres = payload?.mainGenres || [];
  const subGenres = payload?.subGenres || [];
  const level3Genres = payload?.level3Genres || [];
  const level4Genres = payload?.level4Genres || [];
  const level5Genres = payload?.level5Genres || [];

  const genres: IGenreValuable[] = [];

  // 遍历主品类（从第1层级开始，而不是从 subGenres 开始）
  for (const mainGenre of mainGenres) {
    const genreValue = mainGenre?.value;
    if (!genreValue) continue;

    // 创建主品类节点
    const genre: IGenreValuable = {
      relation: (mainGenre?.relation as Relation) || Relation.Or,
      value: genreValue,
      children: [],
    };

    // 查找该主品类下的副品类
    const subGenresOfMain = subGenres.filter((sg) => sg?.parentValue === genreValue);

    if (subGenresOfMain.length > 0) {
      // 有明确的副品类选中，构建副品类节点
      for (const subGenre of subGenresOfMain) {
        const subGenreValue = subGenre?.value;
        if (!subGenreValue) continue;

        const subGenreNode: any = { value: subGenreValue };

        // 查找该副品类下的第3层级品类
        const level3GenresOfSub = level3Genres.filter(
          (l3) => l3?.parentValue === genreValue && l3?.parent2Value === subGenreValue,
        );

        if (level3GenresOfSub.length > 0) {
          subGenreNode.children = [];
          for (const level3Genre of level3GenresOfSub) {
            const level3GenreValue = level3Genre?.value;
            if (!level3GenreValue) continue;

            const level3GenreNode: any = { value: level3GenreValue };

            // 查找该第3层级品类下的第4层级品类
            const level4GenresOfLevel3 = level4Genres.filter(
              (l4) =>
                l4?.parentValue === genreValue &&
                l4?.parent2Value === subGenreValue &&
                l4?.parent3Value === level3GenreValue,
            );

            if (level4GenresOfLevel3.length > 0) {
              level3GenreNode.children = [];
              for (const level4Genre of level4GenresOfLevel3) {
                const level4GenreValue = level4Genre?.value;
                if (!level4GenreValue) continue;
                const level4GenreNode: any = { value: level4GenreValue };

                // 查找该第4层级品类下的第5层级品类
                const level5GenresOfLevel4 = level5Genres.filter(
                  (l5) =>
                    l5?.parentValue === genreValue &&
                    l5?.parent2Value === subGenreValue &&
                    l5?.parent3Value === level3GenreValue &&
                    l5?.parent4Value === level4GenreValue,
                );

                if (level5GenresOfLevel4.length > 0) {
                  level4GenreNode.children = [];
                  for (const level5Genre of level5GenresOfLevel4) {
                    const level5GenreValue = level5Genre?.value;
                    if (!level5GenreValue) continue;
                    level4GenreNode.children.push({ value: level5GenreValue });
                  }
                }

                level3GenreNode.children.push(level4GenreNode);
              }
            }

            subGenreNode.children.push(level3GenreNode);
          }
        }

        genre.children?.push(subGenreNode);
      }
    }

    genres.push(genre);
  }

  return {
    source,
    genres,
  };
};
